import { useState, useCallback, useEffect } from "react";
import { Endpoint } from "../../../api/base";
import {
  Intake,
  IntakeMessage,
  ChatResponse,
} from "../../../types/AIChatBotTypes";

export interface UseIntakeChatReturn {
  intakes: Intake[];
  activeIntakeId: string | null;
  messages: IntakeMessage[];
  isTyping: boolean;
  loading: boolean;
  isCreating: boolean;
  error: string | null;
  clearError: () => void;
  fetchIntakes: () => Promise<void>;
  selectIntake: (id: string) => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  createIntake: () => Promise<void>;
  closeIntake: (id: string) => Promise<void>;
  updateLabel: (id: string, label: string) => Promise<void>;
  goBackToList: () => void;
}

export const useIntakeChat = (): UseIntakeChatReturn => {
  const [intakes, setIntakes] = useState<Intake[]>([]);
  const [activeIntakeId, setActiveIntakeId] = useState<string | null>(null);
  const [messages, setMessages] = useState<IntakeMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(false); // when fetching intakes or messages
  const [isCreating, setIsCreating] = useState(false); // when creating a new intake
  const [error, setError] = useState<string | null>(null);

  const fetchIntakes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await Endpoint.listIntakes();
      setIntakes(res.data.intakes || []);
    } catch (err) {
      console.error("Failed to fetch intakes:", err);
      setError("Failed to load chats.");
    } finally {
      setLoading(false);
    }
  }, []);

  const selectIntake = useCallback(async (id: string) => {
    setActiveIntakeId(id);
    setMessages([]);
    setLoading(true);
    setError(null);
    try {
      const res = await Endpoint.getIntakeMessages(id);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      setError("Failed to load messages.");
    } finally {
      setLoading(false);
    }
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      // Capture whether this is the first message using the state updater
      // so we always read fresh state, not a stale closure value
      let isFirstMessage = false;
      const userMessage: IntakeMessage = {
        role: "user",
        text,
        created_at: Math.floor(Date.now() / 1000),
      };
      setMessages((prev) => {
        isFirstMessage = prev.length === 0;
        return [...prev, userMessage];
      });
      setIsTyping(true);
      setError(null);

      try {
        const res = await Endpoint.sendChatMessage(activeIntakeId, text);
        const data: ChatResponse = res.data;

        let resolvedIntakeId = activeIntakeId;

        // If no active intake, the backend auto-created one
        if (!activeIntakeId && data.intake_id) {
          setActiveIntakeId(data.intake_id);
          resolvedIntakeId = data.intake_id;
          // Await so the list is populated before we apply the label below
          await fetchIntakes();
        }

        // Label the intake with the first user message if not already labeled
        if (isFirstMessage && resolvedIntakeId) {
          const label =
            text.length > 42 ? text.slice(0, 42).trimEnd() + "…" : text;
          setIntakes((prev) =>
            prev.map((i) =>
              i.intake_id === resolvedIntakeId && !i.label
                ? { ...i, label }
                : i,
            ),
          );
        }

        const assistantMessage: IntakeMessage = {
          role: "assistant",
          text: data.reply,
          created_at: Math.floor(Date.now() / 1000),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err) {
        console.error("Failed to send message:", err);
        setError("Failed to send message.");
      } finally {
        setIsTyping(false);
      }
    },
    [activeIntakeId, fetchIntakes],
  );

  const createIntake = useCallback(async () => {
    setIsCreating(true);
    setError(null);
    try {
      const res = await Endpoint.createIntake();
      const newIntake: Intake = {
        intake_id: res.data.intake_id,
        label: res.data.label,
      };
      setIntakes((prev) => [newIntake, ...prev]);
      setActiveIntakeId(newIntake.intake_id);
      setMessages([]);
    } catch (err) {
      console.error("Failed to create intake:", err);
      setError("Failed to create chat.");
    } finally {
      setIsCreating(false);
    }
  }, []);

  const closeIntake = useCallback(
    async (id: string) => {
      setError(null);
      try {
        await Endpoint.closeIntake(id);
        setIntakes((prev) => prev.filter((i) => i.intake_id !== id));
        if (activeIntakeId === id) {
          setActiveIntakeId(null);
          setMessages([]);
        }
      } catch (err) {
        console.error("Failed to close intake:", err);
        setError("Failed to delete chat.");
      }
    },
    [activeIntakeId],
  );

  const updateLabel = useCallback(async (id: string, label: string) => {
    setError(null);
    try {
      await Endpoint.updateIntakeLabel(id, label);
      setIntakes((prev) =>
        prev.map((i) => (i.intake_id === id ? { ...i, label } : i)),
      );
    } catch (err) {
      console.error("Failed to update label:", err);
      setError("Failed to rename chat.");
    }
  }, []);

  const goBackToList = useCallback(() => {
    setActiveIntakeId(null);
    setMessages([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch intakes on mount
  useEffect(() => {
    fetchIntakes();
  }, [fetchIntakes]);

  return {
    intakes,
    activeIntakeId,
    messages,
    isTyping,
    loading,
    isCreating,
    error,
    clearError,
    fetchIntakes,
    selectIntake,
    sendMessage,
    createIntake,
    closeIntake,
    updateLabel,
    goBackToList,
  };
};

export default useIntakeChat;
